import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { timeout, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { RdsService } from '../../api/rds/rds.service';
import { CacheService } from '../cache/cache.service';

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  private readonly API_PATH = '/api';
  private readonly VIP_PATH = '/vip';
  private readonly MENU_VERSION = '/v1/menu';

  // TTL de 10 minutos - se invalida automaticamente al editar via ABM
  private readonly MENU_CACHE_TTL = 10 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private rdsService: RdsService,
    private cacheService: CacheService,
  ) { }

  get country(): string {
    return this.localStorageService.getRegionSeleccionada?.getCode()?.toUpperCase();
  }

  /**
   * Determina si el usuario está autenticado con VIP (Oidc)
   * Esto afecta qué URL base se usa para operaciones de lectura (GET)
   */
  get isAuthVip(): boolean {
    return this.localStorageService.getIsVip;
  }

  get isRDS(): boolean {
    return this.localStorageService.isMenuRds();
  }

  /**
   * Obtiene el ambiente (env) según el tipo de flujo y configuración
   * - RDS siempre usa DM
   * - Si el config contiene "_VIP_" o termina en "_VIP", usa VIP
   * - Si el menuApp actual es VIP (título), usa VIP
   * - Por defecto, DM
   */
  private getEnv(): string {
    // RDS siempre es interno
    if (this.isRDS) {
      return this.localStorageService.GPC_CONFIG.env.interno;
    }

    // Verificar si el menú actual es VIP por su título
    if (this.localStorageService.isMenuVip()) {
      return this.localStorageService.GPC_CONFIG.env.vip;
    }

    // Por defecto, interno
    return this.localStorageService.GPC_CONFIG.env.interno;
  }

  /**
   * Obtiene la configuración (config) por defecto.
   * En ambiente local retorna "Default" (para mantener compatibilidad con mocks)
   * En otros ambientes retorna "MENU_Default"
   */
  private getDefaultConfig(): string {
    const baseConfig = this.localStorageService.GPC_CONFIG.config.default;

    // En local, usar config simple para mantener compatibilidad con mocks existentes
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      return baseConfig;
    }

    return `MENU_${baseConfig}`;
  }

  /**
   * Construye la URL base para consultas (GET) considerando el flujo de autenticación
   * AUTENTICACIÓN VIP (Oidc): {F_VIP_URL_BASE_API}/vip/gcp-service/v1/menu
   * AUTENTICACIÓN DM (PingID): {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu
   *
   * NOTA: El environment (VIP/DM) en el path se determina por el menu/config que se consulta,
   * no por el tipo de autenticación
   */
  private buildBaseUrlForRead(): string {
    const urlBaseApi = this.isAuthVip
      ? this.localStorageService.configInit["F_VIP_URL_BASE_API"]
      : this.localStorageService.configInit["F_PINGID_URL_BASE_API"];

    if (this.isAuthVip) {
      // Para usuarios autenticados con VIP (Oidc): extraer solo "gcp-service" de "facade-im/gcp-service"
      const fullMicroservicio = this.localStorageService.configInit["F_URL_MS_IM_GCP"];
      const gcpService = fullMicroservicio ? fullMicroservicio.split('/').pop() : "";
      return `${urlBaseApi}${this.VIP_PATH}/${gcpService}${this.MENU_VERSION}`;
    } else {
      // Para usuarios autenticados con Interno (PingID): usar la ruta completa "facade-im/gcp-service"
      const rutaMicroservicio = this.localStorageService.configInit["F_URL_MS_IM_GCP"]

      const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
      return `${urlBaseApi}${microservicioPath}${this.API_PATH}${this.MENU_VERSION}`;
    }
  }

  /**
   * Construye la URL base para operaciones de escritura (POST, DELETE)
   * Siempre usa PINGID (interno), independientemente de si el usuario está en VIP
   * {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu
   */
  private buildBaseUrlForWrite(): string {
    const rutaMicroservicio = (environment.MICROSERVICIO.toString().toLowerCase() === "true")
      ? this.localStorageService.configInit["F_URL_MS_IM_GCP"]
      : "";

    const microservicioPath = rutaMicroservicio ? `/${rutaMicroservicio}` : "";
    return `${this.localStorageService.configInit["F_PINGID_URL_BASE_API"]}${microservicioPath}${this.API_PATH}${this.MENU_VERSION}`;
  }

  /**
   * Consulta Menú Global
   * Método GET
   *
   * Ejemplos de URLs generadas:
   * - Usuario Interno consultando menú INTERNO (DM):
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/AR/DM/MENU_DEFAULT
   *
   * - Usuario Interno consultando menú VIP:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/AR/VIP/MENU_DEFAULT
   *
   * - Usuario VIP consultando menú VIP:
   *   {F_VIP_URL_BASE_API}/vip/gcp-service/v1/menu/AR/VIP/MENU_DEFAULT
   *
   * - RDS (siempre interno):
   *   /api/v1/rds/menu/{country}/{config}
   */
  getMenu(): Observable<any> {

    const config = this.getDefaultConfig()

    // Si es RDS, usar el servicio RDS
    if (this.isRDS) {
      return this.rdsService.getRdsMenu();
    }

    // Si no es RDS, usar el endpoint tradicional de menu
    const baseUrl = this.buildBaseUrlForRead();
    const env = this.getEnv();

    const cacheKey = `menu|${this.country}|${env}|${config}`;
    const cached = this.cacheService.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    const url = `${baseUrl}/${this.country}/${env}/${config}`;

    return this.http.get<any>(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(response => this.cacheService.set(cacheKey, response, this.MENU_CACHE_TTL))
    );
  }

  /**
   * Carga Menú Global
   * Método POST (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Carga menú VIP desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/charge/AR/VIP/MENU_DEFAULT
   *
   * - Carga menú INTERNO (DM) desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/charge/AR/DM/MENU_DEFAULT
   *
   * - RDS:
   *   /api/v1/rds/menu/charge/{country}/{config}
   */
  chargeMenu(body: any): Observable<any> {
    const config = this.getDefaultConfig()

    // Si es RDS, usar el servicio RDS
    if (this.isRDS) {
      return this.rdsService.chargeRdsMenu(config, body);
    }

    // Si no es RDS, usar el endpoint tradicional de menu
    const baseUrl = this.buildBaseUrlForWrite();
    const env = this.getEnv();
    const url = `${baseUrl}/charge/${this.country}/${env}/${config}`;

    return this.http.post<any>(url, body).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateMenuCache())
    );
  }

  /**
   * Consulta de configuraciones
   * Método GET
   *
   * Ejemplos:
   * - Usuario Interno: {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/configs/AR/VIP
   * - Usuario VIP: {F_VIP_URL_BASE_API}/vip/gcp-service/v1/menu/configs/AR/VIP
   * - RDS: /api/v1/rds/menu/configs/{country}
   */
  getMenuConfigs(): Observable<any> {
    // Si es RDS, usar el servicio RDS
    if (this.isRDS) {
      return this.rdsService.getRdsMenuConfigs();
    }

    // Si no es RDS, usar el endpoint tradicional
    if (String(environment.IS_LOCAL.toLowerCase()) === "true") {
      // Modo local: usar mock
      const url = `assets/mock/${this.country.toLowerCase()}/menu/configs.json`;
      return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
    }

    const baseUrl = this.buildBaseUrlForRead();
    const env = this.getEnv();
    const url = `${baseUrl}/configs/${this.country}/${env}`;

    return this.http.get<any>(url).pipe(timeout(Number(environment.HTTP_REQUEST_TIMEOUT)));
  }

  /**
   * Eliminar ítem del menú
   * Método DELETE (siempre usa interno/PINGID para escritura)
   *
   * Ejemplos:
   * - Eliminar de menú VIP desde usuario Interno:
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/AR/VIP/MENU_DEFAULT/items/{itemName}
   *
   * - Eliminar de menú INTERNO (DM):
   *   {F_PINGID_URL_BASE_API}/facade-im/gcp-service/api/v1/menu/AR/DM/MENU_DEFAULT/items/{itemName}
   *
   * - RDS:
   *   /api/v1/rds/menu/{country}/{config}/items/{itemName}
   */
  deleteMenuItem(itemName: string): Observable<any> {
    const config = this.getDefaultConfig();

    console.log('[MenuService.deleteMenuItem] Parámetros:', {
      itemName,
      config,
      country: this.country,
      isRDS: this.isRDS,
      isAuthVip: this.isAuthVip,
      env: this.getEnv()
    });

    // Si es RDS, usar el servicio RDS
    if (this.isRDS) {
      return this.rdsService.deleteRdsMenuItem(config, itemName);
    }

    // Si no es RDS, usar el endpoint tradicional
    const baseUrl = this.buildBaseUrlForWrite();
    const env = this.getEnv();
    const url = `${baseUrl}/${this.country}/${env}/${config}/items/${itemName}`;

    console.log('[MenuService.deleteMenuItem] URL completa:', url);

    return this.http.delete<any>(url).pipe(
      timeout(Number(environment.HTTP_REQUEST_TIMEOUT)),
      tap(() => this.invalidateMenuCache())
    );
  }

  /** Invalida todo el cache de menu */
  invalidateMenuCache(): void {
    this.cacheService.invalidateByPrefix('menu|');
  }
}
